import React, { useContext } from "react";
import profilePreferences from "@coworker/enums/profilePreferences";
import taskTypes from "@coworker/app/src/components/TaskDetail/types";
import widgets from "@coworker/app/src/components/TaskDetail/widgets";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import styles from "./task-detail-sheet.module.css";
import { useTeamId, useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import { useOnline } from "@coworker/app/src/core/hooks/useOnline";
import { AppBar } from "@coworker/apprestructured/src/layout/components/AppBar/AppBar";
import { useTaskOptionPopupMenu } from "@coworker/app/src/features/tasks/useTaskOptionPopupMenu";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import EllipsesHorizontalIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/EllipsesHorizontalIconPath";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import taskStateOptions from "@coworker/enums/taskState";
import { useGetTaskByIdAndType } from "../../../tasks/hooks/useGetTaskByIdAndType";
import { TaskWidgetSkeleton } from "./TaskDetailSkeleton";
import { useTranslation } from "@coworker/locales";

const TaskDetailError = () => {
  const { t } = useTranslation();
  return (
    <div className={styles["task-detail-error"]}>
      <div>{t("sorryTaskNotFoundString")}</div>
    </div>
  );
};

const TaskDetailSheet = () => {
  const { modalState, toggleModal } = useContext(ModalContext);
  const taskId = modalState.props?.taskDetail?.taskId || "";
  const taskType = modalState.props?.taskDetail?.taskType;

  const {
    data: taskData,
    isLoading,
    isFetching,
    isError,
  } = useGetTaskByIdAndType(taskId, taskType);

  const [isDone, setIsDone] = React.useState(false);
  const task = React.useMemo(() => {
    if (!isLoading) {
      return {
        ...taskData,
        isDone,
        setIsDone,
      };
    }
    return {};
  }, [isDone, taskData, isLoading]);

  // Using state to display resolution instantly when a user enters and to avoid any backend slowness on a slow network.
  const [taskResolution, setTaskResolution] = React.useState(
    task?.finished_summary
  );
  const [picking, setPicking] = useUserPreference(
    profilePreferences.PICKING_DEFAULT,
    false
  );

  const { isOnline } = useOnline();
  const uid = useUID();
  const teamId = useTeamId();
  const userAndTeamIds = { uid, team_id: teamId };

  const handleToggleTaskDetailSheet = () => {
    toggleModal({ type: ModalType.TaskDetail });
  };

  const taskTypeConfig = taskTypes[task?.task_type];

  const { getTaskMenu } = useTaskOptionPopupMenu({ ...task, id: taskId });
  const closeMenu = () => {
    toggleModal({ type: ModalType.TaskDetail });
  };

  function openMenu(event) {
    event.stopPropagation();
    getTaskMenu();
  }

  const shouldShowActionsMenu =
    task?.state !== taskStateOptions.COMPLETED &&
    task?.state !== taskStateOptions.CLOSED;

  const availableActions = taskTypeConfig?.actions.filter(({ display }) =>
    display({
      task,
      profile: userAndTeamIds,
      online: isOnline,
      picking: task.task_type === "ADDON" && picking,
    })
  );

  return (
    <FixaModal
      focusLockProps={{
        locked: false,
      }}
      visible={modalState.isTaskDetailSheetOpen}
      keepContentMounted={true}
      handleCloseBtn={handleToggleTaskDetailSheet}
    >
      <FixaSheets
        escapable="true"
        header={
          <div className={styles["task-detail-sheet-mobile-padding"]}>
            <AppBar
              title={""}
              actions={[
                !isLoading &&
                  !isFetching &&
                  !isError &&
                  shouldShowActionsMenu && {
                    name: "menu",
                    icon: <FixaSSRIcon paths={EllipsesHorizontalIconPath} />,
                    position: "right",
                    onClick: openMenu,
                  },
                {
                  name: "closeButton",
                  icon: <FixaSSRIcon paths={CrossIconPath} />,
                  position: "left",
                  onClick: closeMenu,
                },
              ]}
            />
          </div>
        }
        footer={
          <div>
            {!isLoading &&
              !isFetching &&
              !isError &&
              availableActions?.length > 0 && (
                <FixaModalFooter stacked={true} renderBorder>
                  {availableActions?.map(({ component: Action }, key) => (
                    <Action task={task} taskId={taskId} key={key} />
                  ))}
                </FixaModalFooter>
              )}
          </div>
        }
      >
        {(isLoading || isFetching) && <TaskWidgetSkeleton />}
        {!isLoading &&
          !isFetching &&
          !isError &&
          task &&
          task.task_type &&
          taskTypeConfig?.fields.flatMap((field, idx) => {
            const Widget = widgets[field.type];
            return [
              <Widget
                backArrowIsNeeded={false}
                needSticky={false}
                useAppBarMenu={false}
                picking={picking}
                setPicking={setPicking}
                key={field.type + idx}
                value={(field.values?.in || ((id) => id))(task)}
                taskId={taskId}
                task={task}
                taskPath={`${
                  task?.task_type === "PLANNED" ? "repeatable_tasks" : "tasks"
                }/${taskId}/`}
                conf={field.conf}
                initialResolution={taskResolution || task?.finished_summary}
                onChange={(resolution) =>
                  resolution !== undefined && setTaskResolution(resolution)
                }
              />,
            ];
          })}
        {isError && <TaskDetailError />}
      </FixaSheets>
    </FixaModal>
  );
};

export default TaskDetailSheet;
